import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Snackbar, Alert, Chip, CircularProgress, Tooltip, Grid, Card, CardContent } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, Delete, Refresh, Warning, CheckCircle, Key, Settings, Search, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getAPIProviders, createAPIProvider, updateAPIProvider, deleteAPIProvider } from '../../services/apiManagement';
import { useDebounce } from '../../hooks/useDebounce';

const defaultForm = {
  name: '',
  description: '',
  base_url: '',
  documentation_url: '',
  is_active: true
};

const APIManagement = () => {
  const { t } = useTranslation();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Pagination and filtering states
  const [page, setPage] = useState(0); // 0-based for frontend, 1-based for backend
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Calculate total pages and total count
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Debounced search term
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const fetchProviders = useCallback(async (pageArg, rowsArg, search = '', status = 'all') => {
    setLoading(true);
    try {
      const response = await getAPIProviders({
        page: pageArg + 1, // backend expects 1-based
        page_size: rowsArg,
        search,
        status: status === 'all' ? '' : status
      });
      
      if (response.data) {
        setProviders(response.data.results || []);
        setTotalPages(response.data.total_pages || 1);
        setTotalCount(response.data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching API providers:', error);
      setSnackbar({
        open: true,
        message: t('apiManagement:messages.fetchError'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-apply filters when debounced values change
  useEffect(() => {
    setPage(0); // Reset to first page when filters change
    fetchProviders(0, rowsPerPage, debouncedSearchTerm, statusFilter);
  }, [debouncedSearchTerm, statusFilter, rowsPerPage, fetchProviders]);

  // Handle pagination changes (only when page > 0 to avoid double fetch)
  useEffect(() => {
    if (page > 0) {
      fetchProviders(page, rowsPerPage, debouncedSearchTerm, statusFilter);
    }
  }, [page, fetchProviders, rowsPerPage, debouncedSearchTerm, statusFilter]);

  // Refresh function with current filters
  const handleRefresh = () => {
    setPage(0);
    fetchProviders(0, rowsPerPage, debouncedSearchTerm, statusFilter);
  };

  // Pagination handlers
  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };
  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };
  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
      setCustomRowsActive(false);
    }
  };
  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };
  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setRowsPerPage(val);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  // Search and filter handlers
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleOpen = (provider = null) => {
    if (provider) {
      setForm(provider);
      setEditingId(provider.id);
    } else {
      setForm(defaultForm);
      setEditingId(null);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setForm(defaultForm);
    setEditingId(null);
  };

  const handleChange = (field) => (event) => {
    setForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        await updateAPIProvider(editingId, form);
        setSnackbar({
          open: true,
          message: t('apiManagement:messages.updatedSuccess'),
          severity: 'success'
        });
      } else {
        await createAPIProvider(form);
        setSnackbar({
          open: true,
          message: t('apiManagement:messages.createdSuccess'),
          severity: 'success'
        });
      }
      handleClose();
      fetchProviders(page, rowsPerPage, debouncedSearchTerm, statusFilter);
    } catch (error) {
      console.error('Error saving API provider:', error);
      setSnackbar({
        open: true,
        message: t('apiManagement:messages.saveError'),
        severity: 'error'
      });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteAPIProvider(deleteId);
      setSnackbar({
        open: true,
        message: t('apiManagement:messages.deletedSuccess'),
        severity: 'success'
      });
      setDeleteId(null);
      fetchProviders(page, rowsPerPage, debouncedSearchTerm, statusFilter);
    } catch (error) {
      console.error('Error deleting API provider:', error);
      setSnackbar({
        open: true,
        message: t('apiManagement:messages.deleteError'),
        severity: 'error'
      });
    }
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'success' : 'error';
  };

  // Add serial numbers to providers for the current page
  const addSerialNumbers = (data, page, rowsPerPage) =>
    data.map((row, idx) => ({
      ...row,
      sn: (page * rowsPerPage) + idx + 1
    }));

  const getColumns = () => [
    { field: 'sn', headerName: t('apiManagement:table.sn'), width: 80, sortable: false, filterable: false },
    {
      field: 'name',
      headerName: t('apiManagement:table.providerName'),
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {params.value}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {params.row.description}
          </Typography>
        </Box>
      )
    },
    {
      field: 'base_url',
      headerName: t('apiManagement:table.baseUrl'),
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" color="textSecondary" sx={{ wordBreak: 'break-all' }}>
          {params.value || 'N/A'}
        </Typography>
      )
    },
    {
      field: 'api_keys_count',
      headerName: t('apiManagement:table.apiKeys'),
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color="primary"
          variant="outlined"
        />
      )
    },
    {
      field: 'active_keys_count',
      headerName: t('apiManagement:table.activeKeys'),
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color="success"
          variant="outlined"
        />
      )
    },
    {
      field: 'is_active',
      headerName: t('apiManagement:table.status'),
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('apiManagement:status.active') : t('apiManagement:status.inactive')}
          size="small"
          color={getStatusColor(params.value)}
        />
      )
    },
    {
      field: 'created_at',
      headerName: t('apiManagement:table.created'),
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" color="textSecondary">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: t('apiManagement:table.actions'),
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={t('apiManagement:tooltips.edit')}>
            <IconButton
              size="small"
              onClick={() => handleOpen(params.row)}
              color="primary"
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('apiManagement:tooltips.delete')}>
            <IconButton
              size="small"
              onClick={() => setDeleteId(params.row.id)}
              color="error"
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {t('apiManagement:title')}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {t('apiManagement:subtitle')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
          >
            {t('apiManagement:refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
          >
            {t('apiManagement:addProvider')}
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                placeholder={t('apiManagement:filters.searchPlaceholder')}
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                select
                label={t('apiManagement:filters.status')}
                value={statusFilter}
                onChange={handleStatusFilterChange}
                size="small"
              >
                <MenuItem value="all">{t('apiManagement:status.all')}</MenuItem>
                <MenuItem value="active">{t('apiManagement:status.active')}</MenuItem>
                <MenuItem value="inactive">{t('apiManagement:status.inactive')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {providers.length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('apiManagement:stats.totalProviders')}
                  </Typography>
                </Box>
                <Settings color="primary" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {providers.filter(p => p.is_active).length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('apiManagement:stats.activeProviders')}
                  </Typography>
                </Box>
                <CheckCircle color="success" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {providers.reduce((sum, p) => sum + (p.api_keys_count || 0), 0)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('apiManagement:stats.totalApiKeys')}
                  </Typography>
                </Box>
                <Key color="primary" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {providers.reduce((sum, p) => sum + (p.active_keys_count || 0), 0)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('apiManagement:stats.activeKeys')}
                  </Typography>
                </Box>
                <CheckCircle color="success" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Providers Table */}
      <Card sx={{ backgroundColor: 'background.paper' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minHeight: 520,
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: 'rgba(0,0,0,0.5)',
            },
          }}>
            {providers.length === 0 && !loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column' }}>
                <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
                  {t('apiManagement:noProviders')}
                </Typography>
                <Button variant="outlined" onClick={handleRefresh}>
                  {t('apiManagement:refreshData')}
                </Button>
              </Box>
            ) : (
              <DataGrid
                rows={addSerialNumbers(providers, page, rowsPerPage)}
                columns={getColumns()}
                pageSize={rowsPerPage}
                rowsPerPageOptions={[]}
                disableSelectionOnClick
                loading={loading}
                hideFooter
                sx={{
                  border: 'none',
                  minWidth: 1200, // Ensure minimum width for all columns
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: theme => theme.palette.primary.main,
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                    color: 'white'
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#FFF0F3'
                  },
                  '& .MuiDataGrid-cell': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    padding: { xs: '8px 4px', sm: '8px 12px' }
                  }
                }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Custom Pagination Bar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: { xs: 'center', sm: 'flex-end' }, 
        mt: 3, 
        gap: 2,
        flexWrap: 'wrap'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrevPage} disabled={page === 0}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">
            {t('apiManagement:pagination.page')} {totalPages === 0 ? 0 : page + 1} {t('apiManagement:pagination.of')} {totalPages}
          </Typography>
          <IconButton onClick={handleNextPage} disabled={page >= totalPages - 1}>
            <ChevronRight />
          </IconButton>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            {t('apiManagement:pagination.rowsPerPage')}
          </Typography>
          {!customRowsActive ? (
            <TextField
              select
              size="small"
              value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
              onChange={handleRowsPerPageChange}
              sx={{ width: 100 }}
            >
              {[10, 20, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
              <MenuItem value="custom">{t('apiManagement:pagination.custom')}</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              value={customRowsInput}
              onChange={handleCustomRowsInputChange}
              onBlur={handleCustomRowsInputBlur}
              autoFocus
              sx={{ width: 100 }}
              inputProps={{ min: 1, style: { textAlign: 'center' } }}
              placeholder="Rows"
            />
          )}
        </Box>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingId ? t('apiManagement:dialog.editTitle') : t('apiManagement:dialog.addTitle')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  label={t('apiManagement:dialog.providerName')}
                  value={form.name}
                  onChange={handleChange('name')}
                  fullWidth
                  required
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  label={t('apiManagement:dialog.description')}
                  value={form.description}
                  onChange={handleChange('description')}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label={t('apiManagement:dialog.baseUrl')}
                  value={form.base_url}
                  onChange={handleChange('base_url')}
                  fullWidth
                  placeholder={t('apiManagement:dialog.baseUrlPlaceholder')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label={t('apiManagement:dialog.documentationUrl')}
                  value={form.documentation_url}
                  onChange={handleChange('documentation_url')}
                  fullWidth
                  placeholder={t('apiManagement:dialog.documentationUrlPlaceholder')}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('apiManagement:dialog.cancel')}</Button>
          <Button onClick={handleSave} variant="contained">
            {editingId ? t('apiManagement:dialog.update') : t('apiManagement:dialog.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>{t('apiManagement:delete.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('apiManagement:delete.message')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>{t('apiManagement:dialog.cancel')}</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('apiManagement:delete.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default APIManagement;